import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ID from 'react-form-builder2/lib/UUID';
import { AiConfigKey, AiGeneratorAiConfig, AiGeneratorBlockConfig, AiGeneratorOutputFormat, AiGeneratorPrompt } from '../../types';
import {
  AI_TYPES,
  LANGUAGES,
  MODELS,
  TONES,
  getApplicableGoals,
} from '../CustomBlocks/aiGenerator/aiConfig';
import { FabaiPersona, fetchPersonas, personaDisplayName, personaId, personaName, resolvePersonaId } from '../../services/api/fabaiApi';

interface AiGeneratorBlocksEditorProps {
  blocks: AiGeneratorBlockConfig[];
  outputFormat: AiGeneratorOutputFormat;
  mainInputLabel: string;
  /** The author's default generation settings for this field. */
  aiConfig: AiGeneratorAiConfig;
  /** Which of those settings the respondent may change, per key. */
  respondentEditable: Record<AiConfigKey, boolean>;
  /** Author-defined prompt chips shown above the textarea on the live form:
   * the chip shows `label`, clicking it fills the textarea with `value`. */
  prompts: AiGeneratorPrompt[];
  /** The form's own field names — for the `{{name}}` insert buttons. */
  formFieldNames: string[];
  onChange: (next: {
    blocks: AiGeneratorBlockConfig[];
    outputFormat: AiGeneratorOutputFormat;
    mainInputLabel: string;
    aiConfig: AiGeneratorAiConfig;
    respondentEditable: Record<AiConfigKey, boolean>;
    prompts: AiGeneratorPrompt[];
  }) => void;
}

const emptyBlock = (): AiGeneratorBlockConfig => ({
  id: ID.uuid(),
  label: '',
  description: '',
  prompt: '',
  regenerable: true,
  editable: true,
});

const OUTPUT_FORMATS: { value: AiGeneratorOutputFormat; label: string; hint: string }[] = [
  { value: 'markdown', label: 'Markdown (label as heading, output below)', hint: '## Label\\n\\nOutput text' },
  { value: 'json', label: 'JSON ([{ label, output }])', hint: '[{"label":"...","output":"..."}]' },
  { value: 'blocks-only', label: 'Just the outputs, no labels', hint: 'Output text only, one block per paragraph' },
];

/**
 * Configures an AI Generator field: its content blocks, prefilled prompt
 * chips, how the final value is composed, and the author's default AI
 * settings (type, tone, goal, persona, model, language) — each with its own
 * "respondents can change" toggle. The block prompts are part of the form's
 * saved data, which the browser already receives to render the form — the AI
 * request is built client-side (see CustomBlocks/AiGenerator.tsx).
 */
export const AiGeneratorBlocksEditor: React.FC<AiGeneratorBlocksEditorProps> = ({
  blocks,
  outputFormat,
  mainInputLabel,
  aiConfig,
  respondentEditable,
  prompts: rawPrompts,
  formFieldNames,
  onChange,
}) => {
  // Tolerate plain-string prompts saved by an earlier build — normalize to
  // label/value pairs (both set to the string) so every row is safe to read.
  const prompts: AiGeneratorPrompt[] = (Array.isArray(rawPrompts) ? rawPrompts : []).map(
    (prompt: AiGeneratorPrompt | string) =>
      typeof prompt === 'string'
        ? { label: prompt, value: prompt }
        : { label: String(prompt?.label ?? ''), value: String(prompt?.value ?? '') },
  );
  // Which block's prompt textarea an "insert {{field}}" click should append to.
  const [activeBlockId, setActiveBlockId] = useState<string | null>(blocks[0]?.id ?? null);
  // Which prompt chip's inline editor is open — an index into `prompts`.
  const [editingPrompt, setEditingPrompt] = useState<number | null>(null);

  // Personas come from the CRM (same list the CRM's own AI generator shows).
  // Only loadable when the page URL carries a CRM token; otherwise the persona
  // setting stays a free-text input.
  const { tenantId } = useParams<{ tenantId?: string }>();
  const query = new URLSearchParams(window.location.search);
  const crmJwtToken = query.get('crmJwtToken')?.trim();
  const crmTenantId = query.get('crmTenantId')?.trim();
  const personaTenantId = crmTenantId || tenantId;
  const [personas, setPersonas] = useState<FabaiPersona[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!personaTenantId || !crmJwtToken) return;
    fetchPersonas(personaTenantId, crmJwtToken)
      .then((list) => {
        if (!cancelled) setPersonas(list);
      })
      .catch(() => {
        // No token rights / offline — fall back to the free-text input.
      });
    return () => {
      cancelled = true;
    };
  }, [personaTenantId, crmJwtToken]);

  const emit = (
    next: Partial<{
      blocks: AiGeneratorBlockConfig[];
      outputFormat: AiGeneratorOutputFormat;
      mainInputLabel: string;
      aiConfig: AiGeneratorAiConfig;
      respondentEditable: Record<AiConfigKey, boolean>;
      prompts: AiGeneratorPrompt[];
    }>,
  ) =>
    onChange({
      blocks,
      outputFormat,
      mainInputLabel,
      aiConfig,
      respondentEditable,
      prompts,
      ...next,
    });

  const updateAiConfig = (patch: Partial<AiGeneratorAiConfig>) =>
    emit({ aiConfig: { ...aiConfig, ...patch } });

  const setRespondentEditable = (key: AiConfigKey, checked: boolean) =>
    emit({ respondentEditable: { ...respondentEditable, [key]: checked } });

  const updatePrompt = (index: number, key: keyof AiGeneratorPrompt, value: string) =>
    emit({
      prompts: prompts.map((prompt, i) => (i === index ? { ...prompt, [key]: value } : prompt)),
    });

  const addPrompt = () => {
    emit({ prompts: [...prompts, { label: '', value: '' }] });
    setEditingPrompt(prompts.length);
  };

  const removePrompt = (index: number) => {
    emit({ prompts: prompts.filter((_, i) => i !== index) });
    setEditingPrompt(null);
  };

  const movePrompt = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= prompts.length) return;
    const next = prompts.slice();
    [next[index], next[target]] = [next[target], next[index]];
    emit({ prompts: next });
    // Keep the same chip's editor open after the swap.
    setEditingPrompt((current) =>
      current === index ? target : current === target ? index : current,
    );
  };

  const handleTypeChange = (type: string) => {
    const goals = getApplicableGoals(type);
    // A goal that doesn't apply to the new type would be silently dropped by
    // the AI service's own grouping — swap it for the new list's first goal.
    const goal = goals.includes(aiConfig.goal) ? aiConfig.goal : goals[0] || '';
    updateAiConfig({ type, goal });
  };

  const goalOptions = getApplicableGoals(aiConfig.type);
  const shownGoals = goalOptions.includes(aiConfig.goal) ? goalOptions : [aiConfig.goal, ...goalOptions].filter(Boolean);

  // Options carry the CRM id as the value (what the AI service and workflow
  // variables want) and the persona's name purely for display.
  const personaOptions =
    personas && personas.length > 0
      ? personas
          .filter((persona) => personaId(persona))
          .map((persona) => ({ value: personaId(persona), label: personaName(persona) }))
      : null;
  // Older forms stored the display name — resolve to the id for the select.
  const personaValue = resolvePersonaId(aiConfig.persona, personas);
  const personaKnown = Boolean(
    personaOptions && personaOptions.some((option) => option.value === personaValue),
  );

  const updateBlock = (id: string, patch: Partial<AiGeneratorBlockConfig>) =>
    emit({ blocks: blocks.map((block) => (block.id === id ? { ...block, ...patch } : block)) });

  const removeBlock = (id: string) => emit({ blocks: blocks.filter((block) => block.id !== id) });

  const addBlock = () => {
    const block = emptyBlock();
    setActiveBlockId(block.id);
    emit({ blocks: [...blocks, block] });
  };

  const moveBlock = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = blocks.slice();
    [next[index], next[target]] = [next[target], next[index]];
    emit({ blocks: next });
  };

  const insertToken = (name: string) => {
    if (!activeBlockId) return;
    const block = blocks.find((b) => b.id === activeBlockId);
    if (!block) return;
    updateBlock(activeBlockId, { prompt: `${block.prompt}${block.prompt && !block.prompt.endsWith(' ') ? ' ' : ''}{{${name}}}` });
  };

  return (
    <div className="ai-generator-blocks-editor">
      <div className="form-group">
        <label className="control-label">Text box label</label>
        <input
          type="text"
          className="form-control"
          placeholder="e.g. Describe your business"
          value={mainInputLabel}
          onChange={(e) => emit({ mainInputLabel: e.target.value })}
        />
        <small className="form-text text-muted">
          Shown above the textarea every visitor fills in. Its answer, like any form field, can
          be used in a block's prompt as <code>{'{{' + '<this field\'s name>' + '}}'}</code>.
        </small>
      </div>

      <div className="form-group">
        <label className="control-label">Final value format</label>
        <select
          className="form-control"
          value={outputFormat}
          onChange={(e) => emit({ outputFormat: e.target.value as AiGeneratorOutputFormat })}
        >
          {OUTPUT_FORMATS.map((format) => (
            <option key={format.value} value={format.value}>
              {format.label}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">
          How the generated blocks are combined into what this field submits.
        </small>
      </div>

      <label className="control-label">AI settings</label>
      <small className="form-text text-muted" style={{ display: 'block', marginBottom: 8 }}>
        The defaults every generation uses. Tick "Respondents can change" on any setting to show it
        as an editable chip on the live form.
      </small>

      <div className="form-group">
        <div className="ai-setting-head">
          <label className="control-label" style={{ marginBottom: 0 }}>
            Type
          </label>
          <div className="ai-setting-toggle">
            <input
              type="checkbox"
              checked={respondentEditable.type}
              onChange={(e) => setRespondentEditable('type', e.target.checked)}
            />{' '}
            Respondents can change
          </div>
        </div>
        <select
          className="form-control"
          value={
            AI_TYPES.some((option) => option.value === aiConfig.type)
              ? aiConfig.type
              : AI_TYPES[0]?.value || ''
          }
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {!AI_TYPES.some((option) => option.value === aiConfig.type) && aiConfig.type && (
            <option value={aiConfig.type}>{aiConfig.type}</option>
          )}
          {AI_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">What the AI is asked to write.</small>
      </div>

      <div className="form-group">
        <div className="ai-setting-head">
          <label className="control-label" style={{ marginBottom: 0 }}>
            Tone
          </label>
          <div className="ai-setting-toggle">
            <input
              type="checkbox"
              checked={respondentEditable.tone}
              onChange={(e) => setRespondentEditable('tone', e.target.checked)}
            />{' '}
            Respondents can change
          </div>
        </div>
        <select
          className="form-control"
          value={aiConfig.tone}
          onChange={(e) => updateAiConfig({ tone: e.target.value })}
        >
          {!TONES.includes(aiConfig.tone) && aiConfig.tone && (
            <option value={aiConfig.tone}>{aiConfig.tone}</option>
          )}
          {TONES.map((tone) => (
            <option key={tone} value={tone}>
              {tone}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <div className="ai-setting-head">
          <label className="control-label" style={{ marginBottom: 0 }}>
            Goal
          </label>
          <div className="ai-setting-toggle">
            <input
              type="checkbox"
              checked={respondentEditable.goal}
              onChange={(e) => setRespondentEditable('goal', e.target.checked)}
            />{' '}
            Respondents can change
          </div>
        </div>
        <select
          className="form-control"
          value={aiConfig.goal}
          onChange={(e) => updateAiConfig({ goal: e.target.value })}
        >
          {shownGoals.map((goal) => (
            <option key={goal} value={goal}>
              {goal}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">The options depend on the chosen type.</small>
      </div>

      <div className="form-group">
        <div className="ai-setting-head">
          <label className="control-label" style={{ marginBottom: 0 }}>
            Persona
          </label>
          <div className="ai-setting-toggle">
            <input
              type="checkbox"
              checked={respondentEditable.persona}
              onChange={(e) => setRespondentEditable('persona', e.target.checked)}
            />{' '}
            Respondents can change
          </div>
        </div>
        {personaOptions ? (
          <select
            className="form-control"
            value={personaValue}
            onChange={(e) => updateAiConfig({ persona: e.target.value })}
          >
            <option value="">None</option>
            {personaValue && !personaKnown ? (
              <option value={personaValue}>{personaDisplayName(aiConfig.persona, personas)}</option>
            ) : null}
            {personaOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Growth Marketing Manager"
            value={aiConfig.persona}
            onChange={(e) => updateAiConfig({ persona: e.target.value })}
          />
        )}
        <small className="form-text text-muted">
          {personaOptions
            ? 'Personas from your CRM workspace.'
            : 'Your CRM personas appear here when the form URL carries a crmJwtToken; until then, type one.'}
        </small>
      </div>

      <div className="form-group">
        <div className="ai-setting-head">
          <label className="control-label" style={{ marginBottom: 0 }}>
            Model
          </label>
          <div className="ai-setting-toggle">
            <input
              type="checkbox"
              checked={respondentEditable.model}
              onChange={(e) => setRespondentEditable('model', e.target.checked)}
            />{' '}
            Respondents can change
          </div>
        </div>
        <select
          className="form-control"
          value={aiConfig.model}
          onChange={(e) => updateAiConfig({ model: e.target.value })}
        >
          {!MODELS.some((model) => model.value === aiConfig.model) && aiConfig.model && (
            <option value={aiConfig.model}>{aiConfig.model}</option>
          )}
          {MODELS.map((model) => (
            <option key={model.value} value={model.value}>
              {model.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <div className="ai-setting-head">
          <label className="control-label" style={{ marginBottom: 0 }}>
            Language
          </label>
          <div className="ai-setting-toggle">
            <input
              type="checkbox"
              checked={respondentEditable.language}
              onChange={(e) => setRespondentEditable('language', e.target.checked)}
            />{' '}
            Respondents can change
          </div>
        </div>
        <select
          className="form-control"
          value={aiConfig.language}
          onChange={(e) => updateAiConfig({ language: e.target.value })}
        >
          {!LANGUAGES.some((language) => language.value === aiConfig.language) && aiConfig.language && (
            <option value={aiConfig.language}>{aiConfig.language}</option>
          )}
          {LANGUAGES.map((language) => (
            <option key={language.value} value={language.value}>
              {language.label}
            </option>
          ))}
        </select>
      </div>

      <label className="control-label">Prefilled prompts</label>
      <small className="form-text text-muted" style={{ display: 'block', marginBottom: 8 }}>
        Optional chips shown above the textarea on the live form. Click a chip to edit it — the{' '}
        <strong>label</strong> is what visitors see on the chip, the <strong>value</strong> is
        what fills the textarea.
      </small>

      <div className="ai-prompt-chipstrip" role="group" aria-label="Prefilled prompts">
        {prompts.map((prompt, index) => {
          const hasValue = Boolean(prompt.label.trim() || prompt.value.trim());
          const selected = editingPrompt === index;
          return (
            <button
              key={index}
              type="button"
              className={`ai-prompt-select-chip${selected ? ' is-selected' : ''}${
                hasValue ? '' : ' is-empty'
              }`}
              aria-expanded={selected}
              title={prompt.value || 'Empty prompt'}
              onClick={() => setEditingPrompt(selected ? null : index)}
            >
              <span className="ai-prompt-select-chip-text">
                {prompt.label || prompt.value || 'Untitled'}
              </span>
            </button>
          );
        })}
        <button type="button" className="ai-prompt-add-chip" onClick={addPrompt}>
          + Add prompt
        </button>
      </div>

      {editingPrompt !== null && prompts[editingPrompt] && (
        <div
          className="ai-prompt-edit"
          role="group"
          aria-label="Edit prompt"
          onKeyDown={(event) => {
            if (event.key === 'Escape') setEditingPrompt(null);
          }}
        >
          <div className="ai-prompt-edit-field">
            <label className="ai-prompt-edit-label" htmlFor={`ai-prompt-label-${editingPrompt}`}>
              Label
            </label>
            <input
              id={`ai-prompt-label-${editingPrompt}`}
              type="text"
              className="form-control"
              placeholder="What visitors see on the chip"
              value={prompts[editingPrompt].label}
              onChange={(event) => updatePrompt(editingPrompt, 'label', event.target.value)}
            />
          </div>
          <div className="ai-prompt-edit-field">
            <label className="ai-prompt-edit-label" htmlFor={`ai-prompt-value-${editingPrompt}`}>
              Value
            </label>
            <textarea
              id={`ai-prompt-value-${editingPrompt}`}
              className="form-control"
              rows={3}
              placeholder="What fills the textarea when the chip is clicked"
              value={prompts[editingPrompt].value}
              onChange={(event) => updatePrompt(editingPrompt, 'value', event.target.value)}
            />
          </div>
          <div className="ai-prompt-edit-actions">
            <div className="ai-prompt-edit-order">
              <button
                type="button"
                disabled={editingPrompt === 0}
                onClick={() => movePrompt(editingPrompt, -1)}
              >
                ← Earlier
              </button>
              <button
                type="button"
                disabled={editingPrompt === prompts.length - 1}
                onClick={() => movePrompt(editingPrompt, 1)}
              >
                Later →
              </button>
            </div>
            <div className="ai-prompt-edit-actions-right">
              <button
                type="button"
                className="ai-prompt-edit-remove"
                onClick={() => removePrompt(editingPrompt)}
              >
                Remove
              </button>
              <button
                type="button"
                className="ai-prompt-edit-done"
                onClick={() => setEditingPrompt(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <label className="control-label" style={{ display: 'block', marginTop: 16 }}>
        Content blocks
      </label>
      <small className="form-text text-muted" style={{ display: 'block', marginBottom: 8 }}>
        Each block is generated (or regenerated) from its own prompt. A prompt can use{' '}
        <code>{'{{fieldName}}'}</code> for any field on the form, including this one.
      </small>

      {formFieldNames.length > 0 && (
        <div className="ai-blocks-token-row">
          <span className="ai-blocks-token-hint">Insert into the selected block's prompt:</span>
          {formFieldNames.map((name) => (
            <button
              key={name}
              type="button"
              className="ai-blocks-token-chip"
              disabled={!activeBlockId}
              onClick={() => insertToken(name)}
            >
              {'{{' + name + '}}'}
            </button>
          ))}
        </div>
      )}

      {blocks.length === 0 && (
        <div className="ai-blocks-empty">No blocks yet. Add one to start generating content.</div>
      )}

      {blocks.map((block, index) => (
        <div
          key={block.id}
          className={`ai-block-row${activeBlockId === block.id ? ' ai-block-row--active' : ''}`}
          onFocus={() => setActiveBlockId(block.id)}
        >
          <div className="ai-block-row-head">
            <span className="ai-block-row-index">{index + 1}</span>
            <div className="ai-block-row-move">
              <button type="button" disabled={index === 0} onClick={() => moveBlock(index, -1)} aria-label="Move up">
                ↑
              </button>
              <button
                type="button"
                disabled={index === blocks.length - 1}
                onClick={() => moveBlock(index, 1)}
                aria-label="Move down"
              >
                ↓
              </button>
            </div>
            <button type="button" className="ai-block-row-remove" onClick={() => removeBlock(block.id)}>
              Remove
            </button>
          </div>

          <input
            type="text"
            className="form-control"
            placeholder="Label (e.g. Primary Objective)"
            value={block.label}
            onChange={(e) => updateBlock(block.id, { label: e.target.value })}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Description shown to the visitor (optional)"
            value={block.description || ''}
            onChange={(e) => updateBlock(block.id, { description: e.target.value })}
            style={{ marginTop: 6 }}
          />
          <textarea
            className="form-control"
            placeholder="Prompt sent to the AI, e.g. Given {{business}}, state the primary objective."
            rows={2}
            value={block.prompt}
            onChange={(e) => updateBlock(block.id, { prompt: e.target.value })}
            style={{ marginTop: 6 }}
          />

          <div>
            <div className="ai-block-row-flag">
              <input
                type="checkbox"
                checked={block.regenerable !== false}
                onChange={(e) => updateBlock(block.id, { regenerable: e.target.checked })}
              />{' '}
              Visitor can regenerate this block on its own
            </div>
            <div className="ai-block-row-flag">
              <input
                type="checkbox"
                checked={block.editable !== false}
                onChange={(e) => updateBlock(block.id, { editable: e.target.checked })}
              />{' '}
              Visitor can edit the generated text
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="ai-blocks-add" onClick={addBlock}>
        + Add block
      </button>

      <style>{`
        .ai-generator-blocks-editor .ai-blocks-token-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
        }
        .ai-generator-blocks-editor .ai-blocks-token-hint {
          font-size: 12px;
          color: var(--admin-text-muted);
          margin-right: 4px;
        }
        .ai-generator-blocks-editor .ai-blocks-token-chip {
          font-size: 11.5px;
          font-family: monospace;
          background: var(--admin-accent-soft);
          border: 1px solid var(--primary-200);
          color: var(--admin-accent-hover);
          border-radius: 4px;
          padding: 2px 6px;
          cursor: pointer;
        }
        .ai-generator-blocks-editor .ai-blocks-token-chip:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .ai-generator-blocks-editor .ai-blocks-empty {
          font-size: 13px;
          color: var(--admin-text-muted);
          padding: 8px 0;
        }
        .ai-generator-blocks-editor .ai-setting-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 4px;
        }
        .ai-generator-blocks-editor .ai-setting-toggle {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 500;
          color: var(--admin-accent);
          margin-bottom: 0;
          cursor: pointer;
          white-space: nowrap;
        }
        /* Prefilled-prompt chips — same pill vocabulary as SettingChip. */
        .ai-generator-blocks-editor .ai-prompt-chipstrip {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip,
        .ai-generator-blocks-editor .ai-prompt-add-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 32px;
          box-sizing: border-box;
          padding: 4px 8px 4px 12px;
          border: 1px solid var(--primary-300);
          border-radius: 16px;
          background: var(--admin-surface);
          font-size: 13px;
          font-family: inherit;
          color: var(--primary-700);
          cursor: pointer;
          outline: none;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip:hover,
        .ai-generator-blocks-editor .ai-prompt-add-chip:hover {
          background: var(--admin-accent-soft);
          border-color: var(--primary-400);
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip:focus-visible,
        .ai-generator-blocks-editor .ai-prompt-add-chip:focus-visible {
          outline: 2px solid var(--primary-200);
          outline-offset: 2px;
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip-text {
          font-weight: 500;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip svg {
          color: var(--admin-text-muted);
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip.is-empty {
          border-color: var(--admin-border-strong);
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip.is-empty .ai-prompt-select-chip-text {
          color: var(--admin-text-faint);
        }
        .ai-generator-blocks-editor .ai-prompt-select-chip.is-selected {
          border-color: var(--primary-700);
          background: var(--admin-accent-soft);
        }
        .ai-generator-blocks-editor .ai-prompt-add-chip {
          padding: 4px 12px;
          font-weight: 600;
          border-style: dashed;
        }
        .ai-generator-blocks-editor .ai-prompt-edit {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 12px;
          margin-bottom: 12px;
          border: 1px solid var(--admin-border-strong);
          border-radius: 8px;
          background: var(--admin-surface);
          transition: border-color 0.15s ease;
        }
        .ai-generator-blocks-editor .ai-prompt-edit:focus-within {
          border-color: var(--primary-300);
        }
        .ai-generator-blocks-editor .ai-prompt-edit-field {
          display: flex;
          flex-direction: column;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--admin-text-secondary);
          margin-bottom: 4px;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-order {
          display: flex;
          gap: 6px;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-order button {
          font-family: inherit;
          font-size: 12px;
          color: var(--admin-text-secondary);
          background: var(--admin-surface);
          border: 1px solid var(--admin-border-strong);
          border-radius: 6px;
          padding: 4px 8px;
          cursor: pointer;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-order button:hover:not(:disabled) {
          background: var(--admin-hover);
        }
        .ai-generator-blocks-editor .ai-prompt-edit-order button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-actions-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-remove {
          font-family: inherit;
          font-size: 12.5px;
          color: #d32f2f;
          background: none;
          border: none;
          cursor: pointer;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-done {
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 600;
          color: #ffffff;
          background: var(--primary-700);
          border: 1px solid var(--primary-700);
          border-radius: 6px;
          padding: 5px 14px;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .ai-generator-blocks-editor .ai-prompt-edit-done:hover {
          background: var(--primary-800);
        }
        @media (prefers-reduced-motion: reduce) {
          .ai-generator-blocks-editor .ai-prompt-select-chip,
          .ai-generator-blocks-editor .ai-prompt-add-chip,
          .ai-generator-blocks-editor .ai-prompt-edit,
          .ai-generator-blocks-editor .ai-prompt-edit-done {
            transition: none;
          }
        }
        .ai-generator-blocks-editor .ai-block-row {
          border: 1px solid var(--admin-border);
          border-radius: 6px;
          padding: 10px;
          margin-bottom: 10px;
        }
        .ai-generator-blocks-editor .ai-block-row--active {
          border-color: var(--admin-accent);
        }
        .ai-generator-blocks-editor .ai-block-row-head {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }
        .ai-generator-blocks-editor .ai-block-row-index {
          font-size: 11px;
          font-weight: 700;
          color: var(--admin-text-faint);
          width: 16px;
        }
        .ai-generator-blocks-editor .ai-block-row-move {
          display: flex;
          gap: 3px;
        }
        .ai-generator-blocks-editor .ai-block-row-move button {
          font-size: 11px;
          border: 1px solid var(--admin-border-strong);
          background: var(--admin-surface);
          border-radius: 3px;
          padding: 1px 6px;
          cursor: pointer;
        }
        .ai-generator-blocks-editor .ai-block-row-move button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ai-generator-blocks-editor .ai-block-row-remove {
          margin-left: auto;
          font-size: 11.5px;
          color: #d32f2f;
          background: none;
          border: none;
          cursor: pointer;
        }
        .ai-generator-blocks-editor .ai-block-row-flag {
          display: flex;
          flex-direction: row;
          gap: 4px;
          margin-top: 8px;
          font-size: 12.5px;
          color: var(--admin-text-secondary);
        }
        .ai-generator-blocks-editor .ai-blocks-add {
          font-size: 13px;
          font-weight: 600;
          color: var(--admin-accent);
          background: none;
          border: 1px dashed var(--primary-300);
          border-radius: 6px;
          padding: 6px 12px;
          cursor: pointer;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default AiGeneratorBlocksEditor;
